// src/AppRouter.tsx
import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { ErrorBoundary } from 'react-error-boundary'
import ShellLayout from './layouts/ShellLayout'
import AgentTVLayout from './layouts/AgentTvLayout'
import { ProtectedLayout } from './layouts/ProtectedLayout'
import { useTenant } from './providers/TenantProvider'

const Dashboard = lazy(() => import('./pages/DashboardPage'))
const TicketManagement = lazy(() => import('./pages/TicketsPage'))
const Login = lazy(() => import('./pages/LoginPage'))
const Signup = lazy(() => import('./pages/SignupPage'))
const AgentLeaderboardTV = lazy(() => import('./pages/AgentLeaderboardTVPage'))

function Fallback() {
  return <div className='p-4'>Loading…</div>
}

function RouteError({ error }: { error?: Error }) {
  return (
    <div className='p-6'>
      <h2 className='text-lg font-semibold'>Something went wrong</h2>
      {error?.message && <p className='mt-2 text-sm opacity-80'>{error.message}</p>}
    </div>
  )
}

export default function AppRouter() {
  const { orgSlug, isSignedIn } = useTenant()

  // Redirect logic based on role
  const redirectToLeaderboard = orgSlug === 'ps-csr-1' || orgSlug === 'ps-csr-2'

  return (
    <Routes>
      <Route
        path='/login/*'
        element={
          <ErrorBoundary FallbackComponent={RouteError}>
            <Suspense fallback={<Fallback />}>
              <Login />
            </Suspense>
          </ErrorBoundary>
        }
      />

      <Route
        path='/signup/*'
        element={
          <ErrorBoundary FallbackComponent={RouteError}>
            <Suspense fallback={<Fallback />}>
              <Signup />
            </Suspense>
          </ErrorBoundary>
        }
      />

      <Route element={<ProtectedLayout />}>
        {/* Agent TV Layout for leaderboard */}
        <Route element={<AgentTVLayout />}>
          <Route
            path='/leaderboard'
            element={
              <ErrorBoundary FallbackComponent={RouteError}>
                <Suspense fallback={<Fallback />}>
                  <AgentLeaderboardTV />
                </Suspense>
              </ErrorBoundary>
            }
          />
        </Route>

        <Route element={<ShellLayout />}>
          {/* Conditionally redirect based on role */}
          <Route
            path='/'
            element={redirectToLeaderboard ? <Navigate to='/leaderboard' replace /> : (
              <ErrorBoundary FallbackComponent={RouteError}>
                <Suspense fallback={<Fallback />}>
                  <Dashboard />
                </Suspense>
              </ErrorBoundary>
            )}
          />
          <Route
            path='/tickets'
            element={redirectToLeaderboard ? <Navigate to='/leaderboard' replace /> : (
              <ErrorBoundary FallbackComponent={RouteError}>
                <Suspense fallback={<Fallback />}>
                  <TicketManagement />
                </Suspense>
              </ErrorBoundary>
            )}
          />
        </Route>
      </Route>

      <Route
        path='*'
        element={
          <Navigate to={isSignedIn ? '/leaderboard' : '/login'} replace />
        }
      />
    </Routes>
  )
}
